"use client";
import * as React from "react";
import {
  Stack, Typography, Chip, IconButton, Tooltip, Collapse, Box,
} from "@mui/material";
import type { AssistantSubagentPart } from "../../../chat/types";
import { SubagentEventList } from "./SubagentEventList";
import { SubagentResultPreview } from "./SubagentResultPreview";
import { AnimatedCard } from "../../Common/AnimatedCard";
import { subagentLiveActivity } from "./liveActivity";
import { cancelSubagent } from "./cancelSubagent";

// Inline SVGs — no @mui/icons-material dep.
const ExpandMoreIcon = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
    <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </svg>
);
const ExpandLessIcon = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
    <path d="m12 8-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
  </svg>
);
const CancelIcon = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);
// Gear glyph (SVG, not an emoji) so the subagent label uses the same icon
// language as the rest of the UI.
const GearIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.61-.22l-2.49 1a7.3 7.3 0 0 0-1.69-.98l-.38-2.65A.49.49 0 0 0 14 2h-4a.49.49 0 0 0-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1a.5.5 0 0 0-.61.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46c.14.24.43.34.69.22l2.49-1c.52.39 1.08.73 1.69.98l.38 2.65c.04.24.25.42.49.42h4c.24 0 .45-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.26.12.55.02.69-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" />
  </svg>
);

interface Props { part: AssistantSubagentPart }

/** Human label for a granted tool: plugin MCP tools are namespaced
 *  `<plugin>__<server>__<tool>` — the connector ids are operator noise in a
 *  chat card, so show just the tool name. */
function toolLabel(name: string): string {
  const i = name.lastIndexOf("__");
  return i >= 0 ? name.slice(i + 2) : name;
}

const STATUS_COLORS: Record<string, "default" | "primary" | "success" | "error" | "warning"> = {
  running: "primary",
  succeeded: "success",
  failed: "error",
  cancelled: "warning",
};

export function SubagentCard({ part }: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const statusColor = STATUS_COLORS[part.status] ?? "default";

  const onCancel = async () => {
    if (part.status !== "running") return;
    await cancelSubagent(part.job_id);
  };

  // Live activity: while running, surface the latest thinking/tool event inline
  // so the card streams the subagent's progress as the agent's response (not a
  // bare status chip). Terminal states show the result instead.
  const liveActivity = subagentLiveActivity(part);

  return (
    <Box id={`subagent-${part.job_id}`} data-ph-scope="subagent">
    <AnimatedCard variant="raised" sx={{ p: 2, my: 1 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <GearIcon /> {part.name || "Subagent"}
          </Typography>
          <Chip
            label={part.status}
            size="small"
            color={statusColor}
            // Filled chips lose their palette bg to the theme's MuiChip
            // surface override (white label on pale grey = unreadable);
            // outlined is the theme's chip language and keeps colored text.
            variant="outlined"
          />
          {part.current_event_kind && part.status === "running" && (
            <Chip label={`now: ${part.current_event_kind}`} size="small" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {part.status === "running" && (
            <Tooltip title="Cancel">
              <IconButton size="small" data-ph="cancel" onClick={onCancel} aria-label="Cancel">
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expanded ? "Hide events" : "Show events"}>
            <IconButton size="small" data-ph={expanded ? "collapse" : "expand"} onClick={() => setExpanded(!expanded)} aria-label="Toggle events">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          tools: {part.tools.map(toolLabel).join(", ") || "none"}
        </Typography>
      </Box>
      {liveActivity && (
        <Typography
          data-testid="subagent-live-activity"
          variant="body2"
          sx={{ mt: 1, color: "text.secondary", fontStyle: "italic", whiteSpace: "pre-wrap" }}
        >
          {liveActivity}
        </Typography>
      )}
      {part.error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Error: {part.error}
        </Typography>
      )}
      {part.result && <SubagentResultPreview result={part.result} />}
      <Collapse in={expanded}>
        <SubagentEventList events={part.events} />
      </Collapse>
    </AnimatedCard>
    </Box>
  );
}
